import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik, Form, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal, Row, Col, Button } from "react-bootstrap";
import { VscDiffAdded } from "react-icons/vsc";
import { BiTrash } from "react-icons/bi";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import CommonFormHeading from "../CommonUtils/CommonFormHeading";
import { ImagePdfBucket } from "../CommonUtils/ImagesBase64";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import { config } from "../CommonUtils/CommonURLS";
import { viewImage } from "../CommonUtils/viewImage";

const latestNewsValidationSchema = Yup.object().shape({
    latestNewsEntryList: Yup.array().of(
        Yup.object().shape({
            latestNewsDescription: Yup.string().required("Description is required"),
            latestNewsFile: Yup.string().required("PDF is required"),
            latestNewsDate: Yup.string().required("Date is required"),
        })
    ),
});

const createEmptyLatestNewsRow = () => ({
    latestNewsDescription: "",
    latestNewsFile: "",
    latestNewsDate: "",
});

export default function LatestNews() {
    const [isLatestNewsModalOpen, setIsLatestNewsModalOpen] = useState(false);
    const [latestRecords, setlatestRecords] = useState([]);
    const [isNewsLoading, setisNewsLoading] = useState(true);
    const [isNewsEditMode, setisNewsEditMode] = useState(false);
    const [selectedNewsRecordId, setselectedNewsRecordId] = useState(null);
    const [latestNewsReuploadedRows, setLatestNewsReuploadedRows] = useState({});

    const latestNewsFormik = useFormik({
        initialValues: {
            latestNewsEntryList: [createEmptyLatestNewsRow()],
        },
        validationSchema: latestNewsValidationSchema,
        onSubmit: (latestNewsFormValues, { resetForm }) => {
            Swal.fire({
                text: "Are you sure you want to submit?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, submit it!",
                cancelButtonText: "No, cancel!",
                backdrop: false,
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }

                const latestNewsSubmitUrl = isNewsEditMode ? `${config.url.STU_URL}updateLatestNews` : `${config.url.STU_URL}saveLatestNewsEntry`;

                const latestNewsSubmitPayload = isNewsEditMode
                    ? {
                        latestNewsEntryList: latestNewsFormValues.latestNewsEntryList.map((latestNewsFormRow) => ({
                            sno: selectedNewsRecordId,
                            latestNewsDate: latestNewsFormRow.latestNewsDate,
                            latestNewsDescription: latestNewsFormRow.latestNewsDescription,
                            latestNewsFile: latestNewsFormRow.latestNewsFile,
                        })),
                    }
                    : latestNewsFormValues;

                CommonAxiosPost(latestNewsSubmitUrl, latestNewsSubmitPayload).then((response) => {
                    if (response?.data?.scode === "01") {
                        successAlert2(response?.data?.sdesc).then((isConfirmed) => {
                            if (isConfirmed) {
                                resetForm({ values: { latestNewsEntryList: [createEmptyLatestNewsRow()] } });
                                setIsLatestNewsModalOpen(false);
                                setisNewsEditMode(false);
                                setselectedNewsRecordId(null);
                                fetchlatestRecords();
                            }
                        });
                    } else {
                        failureAlert(response?.data?.sdesc || "Unable to save latest news");
                    }
                });
            });
        },
    });

    const fetchlatestRecords = () => {
        setisNewsLoading(true);
        const latestNewsFetchUrl = `${config.url.STU_URL}ov-public/getLatestNews`;

        CommonAxiosGet(latestNewsFetchUrl).then((response) => {
            if (response?.data?.scode === "01") {
                setlatestRecords(response?.data?.data || []);
            } else {
                setlatestRecords([]);
            }
        }).finally(() => {
            setisNewsLoading(false);
        });
    };

    useEffect(() => {
        fetchlatestRecords();
    }, []);

    const openLatestNewsModal = () => {
        setisNewsEditMode(false);
        setselectedNewsRecordId(null);
        setLatestNewsReuploadedRows({});
        latestNewsFormik.resetForm({ values: { latestNewsEntryList: [createEmptyLatestNewsRow()] } });
        setIsLatestNewsModalOpen(true);
    };

    const closeLatestNewsModal = () => {
        setisNewsEditMode(false);
        setselectedNewsRecordId(null);
        setLatestNewsReuploadedRows({});
        latestNewsFormik.resetForm({ values: { latestNewsEntryList: [createEmptyLatestNewsRow()] } });
        setIsLatestNewsModalOpen(false);
    };

    const handleEditLatestNewsRecord = (latestNewsRecord) => {
        const latestNewsRecordId = latestNewsRecord?.sno || null;
        setisNewsEditMode(true);
        setselectedNewsRecordId(latestNewsRecordId);
        setLatestNewsReuploadedRows({});
        latestNewsFormik.setValues({
            latestNewsEntryList: [
                {
                    latestNewsDate: latestNewsRecord?.latest_news_date || "",
                    latestNewsDescription: latestNewsRecord?.latest_news_description || "",
                    latestNewsFile: latestNewsRecord?.latest_news_file || "",
                },
            ],
        });
        setIsLatestNewsModalOpen(true);
    };

    const handleDeleteLatestNewsRecord = (latestNewsRecordId) => {
        if (!latestNewsRecordId) {
            return;
        }

        Swal.fire({
            title: "Delete Latest News",
            text: "Are you sure you want to delete this record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            backdrop: false,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            const latestNewsDeleteUrl = `${config.url.STU_URL}deleteLatestNewsEntry?sno=${latestNewsRecordId}`;

            CommonAxiosPost(latestNewsDeleteUrl).then((response) => {
                if (response?.data?.scode === "01") {
                    successAlert2(response?.data?.sdesc || response?.data?.data).then((isConfirmed) => {
                        if (isConfirmed) {
                            fetchlatestRecords();
                        }
                    })
                } else {
                    failureAlert(response?.data?.sdesc || response?.data?.data || "Unable to delete latest news");
                }
            });
        });
    };

    const renderLatestNewsFormTable = () => (
        <div className="table-responsive">
            <table className="table table-condensed table-bordered table-sm affl-table">
                <thead>
                    <tr>
                        <th>S.No</th>
                        <th>News Date</th>
                        <th>Description</th>
                        <th>Upload PDF</th>
                        {!isNewsEditMode && <th>Action</th>}
                    </tr>
                </thead>
                <tbody>
                    <FieldArray name="latestNewsEntryList">
                        {({ push, remove }) => (
                            <>
                                {latestNewsFormik.values.latestNewsEntryList.map((latestNewsRow, latestNewsRowIndex) => {
                                    const latestNewsPdfUrl = latestNewsRow.latestNewsFile;
                                    const isLatestNewsFileReuploaded = Boolean(latestNewsReuploadedRows[latestNewsRowIndex]);

                                    return (
                                        <tr key={latestNewsRowIndex}>
                                            <td className="text-center align-middle">{latestNewsRowIndex + 1}</td>
                                            <td>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name={`latestNewsEntryList[${latestNewsRowIndex}].latestNewsDate`}
                                                    value={latestNewsFormik.values.latestNewsEntryList[latestNewsRowIndex].latestNewsDate}
                                                    onChange={latestNewsFormik.handleChange}
                                                />
                                                <ErrorMessage
                                                    name={`latestNewsEntryList[${latestNewsRowIndex}].latestNewsDate`}
                                                    component="div"
                                                    className="text-error"
                                                />
                                            </td>
                                            <td>
                                                <textarea
                                                    rows={2}
                                                    className="form-control"
                                                    name={`latestNewsEntryList[${latestNewsRowIndex}].latestNewsDescription`}
                                                    value={latestNewsFormik.values.latestNewsEntryList[latestNewsRowIndex].latestNewsDescription}
                                                    onChange={latestNewsFormik.handleChange}
                                                    placeholder="Enter description"
                                                />
                                                <ErrorMessage
                                                    name={`latestNewsEntryList[${latestNewsRowIndex}].latestNewsDescription`}
                                                    component="div"
                                                    className="text-error"
                                                />
                                            </td>
                                            <td>
                                                {isNewsEditMode && latestNewsPdfUrl && !isLatestNewsFileReuploaded ? (
                                                    <div className="mb-2">
                                                        {/* <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => openLatestNewsPdfInNewTab(latestNewsRow.latestNewsFile)}
                                                        >
                                                            View Existing PDF
                                                        </button> */}
                                                        <a href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                viewImage(latestNewsRow.latestNewsFile);
                                                            }} className="text-primary text-decoration-underline"
                                                        > View Existing PDF </a>
                                                    </div>
                                                ) : null}
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="application/pdf"
                                                        onChange={(event) => {
                                                            const latestNewsFileFieldName = `latestNewsEntryList[${latestNewsRowIndex}].latestNewsFile`;

                                                            setLatestNewsReuploadedRows((previousRows) => ({
                                                                ...previousRows,
                                                                [latestNewsRowIndex]: true,
                                                            }));

                                                            ImagePdfBucket(
                                                                event,
                                                                latestNewsFormik,
                                                                "/Uploads/latestNews/",
                                                                latestNewsFileFieldName,
                                                                `latest_news_${latestNewsRowIndex}`,
                                                                "pdf"
                                                            );
                                                        }}
                                                    />
                                                    {latestNewsPdfUrl && (!isNewsEditMode || isLatestNewsFileReuploaded) ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => viewImage(latestNewsRow.latestNewsFile)}
                                                        >
                                                            <FaEye />
                                                            {/* View PDF */}
                                                        </button>
                                                    ) : null}
                                                </div>
                                                <ErrorMessage
                                                    name={`latestNewsEntryList[${latestNewsRowIndex}].latestNewsFile`}
                                                    component="div"
                                                    className="text-error"
                                                />
                                            </td>
                                            {!isNewsEditMode && (
                                                <td className="text-center align-middle">
                                                    {latestNewsRowIndex === 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => push(createEmptyLatestNewsRow())}
                                                        >
                                                            <VscDiffAdded />
                                                        </button>
                                                    )}
                                                    {latestNewsRowIndex >= 1 && (
                                                        <button
                                                            type="button"
                                                            className=" btn btn-sm btn-danger"
                                                            onClick={() => remove(latestNewsRowIndex)}
                                                        >
                                                            <BiTrash />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </>
                        )}
                    </FieldArray>
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="affl-newdiv">
            <div className="container-fluid px-1 mt-3">
                <CommonFormHeading heading="Latest News" />

                <div className="row mb-3">
                    <div className="col-12">
                        <Row>
                            <Col xs={12} sm={12} md={12} lg={9} xl={9} xxl={9} />
                            <Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <div className="d-flex flex-row justify-content-end">
                                    {latestRecords.length > 0 && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="primary"
                                            onClick={openLatestNewsModal}
                                        >
                                            + Add Latest News
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>

                {isNewsLoading ? (
                    <div className="container shadow-none px-3 m-0 mw-100 pb-5" style={{ backgroundColor: "#FAF7F0" }}>
                        <div className="text-center py-4">Loading...</div>
                    </div>
                ) : latestRecords.length === 0 ? (
                    <div className="container shadow-none m-0 mw-100 pb-5" style={{ backgroundColor: "#FAF7F0" }}>
                        <FormikProvider value={latestNewsFormik}>
                            <Form onSubmit={latestNewsFormik.handleSubmit}>
                                {renderLatestNewsFormTable()}
                                <div className="d-flex justify-content-end mt-2">
                                    <Button type="submit" size="sm" variant="success">
                                        Submit
                                    </Button>
                                </div>
                            </Form>
                        </FormikProvider>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-condensed table-bordered table-sm table-responsive table-stripped affl-table" style={{ tableLayout: "fixed", minWidth: "960px" }}>
                            <colgroup>
                                <col style={{ width: "70px" }} />
                                <col style={{ width: "140px" }} />
                                <col />
                                <col style={{ width: "120px" }} />
                                <col style={{ width: "150px" }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="text-center">S.No</th>
                                    <th className="text-center">News Date</th>
                                    <th className="text-center">Description</th>
                                    <th className="text-center">PDF</th>
                                    <th className="text-center">Edit / Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {latestRecords.map((data, index) => {
                                    const latestNewsRecordId = data?.sno || null;
                                    const latestNewsStoredFile = data?.latest_news_file || "";
                                    const latestNewsFileUrl = latestNewsStoredFile;

                                    return (
                                        <tr key={latestNewsRecordId || index}>
                                            <td className="text-center align-middle">{index + 1}</td>
                                            <td className="text-center align-middle" style={{ whiteSpace: "nowrap" }}>
                                                {data?.latest_news_date || "-"}
                                            </td>
                                            <td className="align-middle" style={{ wordBreak: "break-word" }}>
                                                {data?.latest_news_description || "-"}
                                            </td>
                                            <td className="text-center align-middle" style={{ whiteSpace: "nowrap" }}>
                                                {latestNewsFileUrl ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm"
                                                        onClick={() => viewImage(latestNewsStoredFile)}
                                                    >
                                                        <FaEye className="" />

                                                    </button>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="text-center align-middle" style={{ whiteSpace: "nowrap" }}>
                                                <button className="btn btn-sm btn-primary me-2">
                                                    <FaEdit


                                                        title="Edit"

                                                        onClick={() => handleEditLatestNewsRecord(data)}
                                                    />
                                                </button>
                                                <button className="btn btn-sm btn-danger">
                                                    <FaTrash
                                                        title="Delete"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleDeleteLatestNewsRecord(latestNewsRecordId)}
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <Modal show={isLatestNewsModalOpen} onHide={closeLatestNewsModal} size="xl" centered className="inner-modalnew">
                    <Modal.Header closeButton>
                        <Modal.Title>{isNewsEditMode ? "Edit Latest News" : "Add Latest News"}</Modal.Title>
                    </Modal.Header>
                    <FormikProvider value={latestNewsFormik}>
                        <Form onSubmit={latestNewsFormik.handleSubmit}>
                            <Modal.Body>
                                {renderLatestNewsFormTable()}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" size="sm" onClick={closeLatestNewsModal}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="success" size="sm">
                                    {isNewsEditMode ? "Update" : "Submit"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    </FormikProvider>
                </Modal>
            </div>
        </div>
    );
}
